import { z } from 'zod';

/** The longest question the `search_help` voice tool accepts, in characters. */
export const SEARCH_HELP_MAX_QUERY_LENGTH = 300;

/**
 * Parameters for the `search_help` voice tool: the member's question, as the
 * agent phrased it. Never carries a member identifier — the server resolves the
 * session from the call's binding, and the search reads no member data.
 */
export const searchHelpParamsSchema = z
  .object({
    query: z.string().trim().min(1).max(SEARCH_HELP_MAX_QUERY_LENGTH),
  })
  .strict();
export type SearchHelpParams = z.infer<typeof searchHelpParamsSchema>;

/** One matched knowledge base section, as plain text the agent can speak from. */
export const searchHelpSectionSchema = z.object({
  title: z.string().min(1),
  section: z.string().min(1),
  text: z.string().min(1),
});
export type SearchHelpSection = z.infer<typeof searchHelpSectionSchema>;

/**
 * What the `search_help` server tool returns to the voice agent. The agent
 * parses it, so it stays open to new fields: `found` carries the sections to
 * answer from, `none` and `unavailable` carry `instead` guidance the agent acts
 * on in the same turn.
 */
export const searchHelpResultSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('found'),
    results: z.array(searchHelpSectionSchema).min(1),
  }),
  z.object({ status: z.literal('none'), instead: z.string().min(1) }),
  z.object({ status: z.literal('unavailable'), instead: z.string().min(1) }),
]);
export type SearchHelpResult = z.infer<typeof searchHelpResultSchema>;

/** The arms of a `search_help` call that answered from the index. */
export const HELP_SEARCH_ANSWER_OUTCOMES = ['found', 'none'] as const;
export const helpSearchAnswerOutcomeSchema = z.enum(HELP_SEARCH_ANSWER_OUTCOMES);
export type HelpSearchAnswerOutcome = z.infer<typeof helpSearchAnswerOutcomeSchema>;

/** Why a `search_help` call answered `unavailable` instead of searching. */
export const HELP_SEARCH_FAILURE_CLASSES = [
  'invalid_request',
  'session_unresolved',
  'tool_not_in_agent_set',
  'internal_error',
] as const;
export const helpSearchFailureClassSchema = z.enum(HELP_SEARCH_FAILURE_CLASSES);
export type HelpSearchFailureClass = z.infer<typeof helpSearchFailureClassSchema>;

/**
 * The page ids behind a `found` answer as one analytics value: sorted, comma
 * joined, or `none`. Each id is a knowledge base page id, so the value is a
 * closed vocabulary bounded by the committed index, never member text.
 */
export const helpSearchPageIdsSchema = z
  .string()
  .max(500)
  .regex(/^(none|[a-z0-9-]+\/[a-z0-9-]+(,[a-z0-9-]+\/[a-z0-9-]+)*)$/);
